<template>
  <span v-if="!conversation.messages.length" class="motif-preview-empty">—</span>
  <button v-else type="button" class="motif-preview" :class="{ 'motif-preview--unread': hasNewReply }"
    :title="tooltip" @click.stop="$emit('open')">
    <v-icon size="15" class="motif-preview__icon"
      :icon="conversation.replyCount ? 'mdi-forum-outline' : 'mdi-message-text-outline'" />
    <span class="motif-preview__text">{{ conversation.motif }}</span>
    <span v-if="conversation.replyCount" class="motif-preview__badge">
      <v-icon size="11" icon="mdi-reply" />
      {{ conversation.replyCount }}
    </span>
  </button>
</template>

<script>
import { getConversation } from "@/core/commentaire";

/**
 * Aperçu du motif dans une ligne de tableau : texte nettoyé (sans le JSON)
 * + pastille indiquant le nombre de réponses. Le clic émet « open » sans
 * déclencher l'action de la ligne (annulation, validation…).
 */
export default {
  name: "MotifPreview",
  props: {
    commentaire: { type: [String, Array, Object, Number], default: "" },
    // Qui consulte : « user » (Mon espace) ou « admin » (zone admin)
    viewer: { type: String, default: "user" },
  },
  emits: ["open"],
  computed: {
    conversation() {
      return getConversation(this.commentaire);
    },
    // Le dernier message vient de l'autre partie : il y a quelque chose à lire
    hasNewReply() {
      const { replyCount, lastMessage } = this.conversation;
      return replyCount > 0 && !!lastMessage && lastMessage.role !== this.viewer;
    },
    tooltip() {
      const { motif, replyCount } = this.conversation;
      const replies = replyCount === 0
        ? "Aucune réponse"
        : `${replyCount} réponse${replyCount > 1 ? "s" : ""}`;
      return `${motif}\n\n${replies} · Cliquer pour voir la conversation`;
    },
  },
};
</script>

<style scoped>
.motif-preview {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  max-width: 240px;
  margin-left: -6px;
  padding: 4px 8px 4px 6px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: #616161;
  font: inherit;
  font-size: 12.5px;
  line-height: 1.35;
  text-align: left;
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease;
}

.motif-preview:hover {
  background: #eef0fb;
  color: #3f51b5;
}

.motif-preview:focus-visible {
  outline: 2px solid #3f51b5;
  outline-offset: 1px;
}

.motif-preview__icon {
  flex-shrink: 0;
  color: #9fa8da;
  transition: color 0.15s ease;
}

.motif-preview:hover .motif-preview__icon {
  color: #3f51b5;
}

.motif-preview__text {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.motif-preview__badge {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  flex-shrink: 0;
  padding: 1px 7px 1px 5px;
  border-radius: 999px;
  background: #e8eaf6;
  color: #3f51b5;
  font-size: 11px;
  font-weight: 700;
  line-height: 16px;
}

.motif-preview--unread .motif-preview__badge {
  background: #3f51b5;
  color: #fff;
  box-shadow: 0 0 0 3px rgba(63, 81, 181, 0.14);
}

.motif-preview-empty {
  color: #bdbdbd;
  font-size: 12.5px;
}

@media (max-width: 600px) {
  .motif-preview {
    max-width: 170px;
  }
}
</style>
